import supabase from './supabase.client.js'

/**
 * Read all flashcards for the current authenticated user.
 * RLS policies automatically filter by user_id based on auth.uid().
 * Results are sorted by created_at in descending order (newest first).
 *
 * @returns {Promise<{data: Array|null, error: Error|null}>} Supabase response object
 */
export async function readFlashcards() {
  return await supabase
    .from('flashcards')
    .select('id, question, answer, created_at')
    .order('created_at', { ascending: false })
}

/**
 * Insert multiple flashcards for the current authenticated user.
 * RLS policies automatically set user_id to auth.uid() on insert.
 * Database trigger automatically sets created_at timestamp.
 *
 * @param {Array<{question: string, answer: string}>} batch - Array of flashcard objects
 * @returns {Promise<{data: Array|null, error: Error|null}>} Supabase response object
 */
export async function insertFlashcards(batch) {
  return await supabase.from('flashcards').insert(batch)
}

/**
 * Update a single flashcard by ID.
 * RLS policies ensure the user can only update their own flashcards.
 *
 * @param {string} id - The ID of the flashcard to update
 * @param {Object} updates - Object containing question and answer
 * @param {string} updates.question - Updated question text
 * @param {string} updates.answer - Updated answer text
 * @returns {Promise<{data: Array|null, error: Error|null}>} Supabase response object
 */
export async function updateFlashcard(id, { question, answer }) {
  return await supabase
    .from('flashcards')
    .update({ question, answer })
    .eq('id', id)
}

/**
 * Delete a single flashcard by ID.
 * RLS policies ensure the user can only delete their own flashcards.
 *
 * @param {number} id - The ID of the flashcard to delete
 * @returns {Promise<{data: Array|null, error: Error|null}>} Supabase response object
 */
export async function deleteFlashcard(id) {
  return await supabase.from('flashcards').delete().eq('id', id)
}

/**
 * Delete all flashcards for the current authenticated user.
 * RLS policies automatically filter by user_id based on auth.uid().
 * Uses .not('id', 'is', null) to match all records for the current user.
 *
 * @returns {Promise<{data: Array|null, error: Error|null}>} Supabase response object
 */
export async function deleteAllUserFlashcards() {
  return await supabase.from('flashcards').delete().not('id', 'is', null)
}
